import React, { useEffect, useState } from 'react';
import { Modal, Form, Button, Row, Col } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FaArrowDown, FaArrowUp } from 'react-icons/fa';
import moment from 'moment';

const ACCENT = '#60A5FA';

const CATEGORY_CHOICES = [
  { value: 'salary', label: 'Salary' },
  { value: 'business', label: 'Business / client' },
  { value: 'lend', label: 'Lend / loan' },
  { value: 'debt', label: 'Debt repayment' },
  { value: 'refund', label: 'Refund' },
  { value: 'housing', label: 'Housing' },
  { value: 'subscriptions', label: 'Subscriptions' },
  { value: 'super_market', label: 'Super Market' },
  { value: 'junk_food', label: 'Junk Food' },
  { value: 'transportation', label: 'Transportation' },
  { value: 'medical', label: 'Medical' },
  { value: 'tax', label: 'Tax' },
  { value: 'bank', label: 'Bank' },
  { value: 'atm', label: 'ATM' },
  { value: 'electronics', label: 'Electronics' },
  { value: 'personal', label: 'Personal' },
  { value: 'donations', label: 'Donations' },
  { value: 'tobacco', label: 'Tobacco' },
  { value: 'gambling', label: 'Gambling' },
  { value: 'other', label: 'Other' },
];

const emptyForm = () => ({
  amount: '',
  category: 'other',
  description: '',
  counterparty: '',
  incoming: false,
  datetime: new Date(),
  account: '',
});

const TransactionModal = ({ show, onHide, bankAccounts, onSubmit, initialData = null }) => {
  const [formData, setFormData] = useState(emptyForm());

  useEffect(() => {
    if (!show) return;
    if (initialData) {
      setFormData({
        amount: String(initialData.amount ?? ''),
        category: initialData.category || 'other',
        description: initialData.description || '',
        counterparty: initialData.counterparty || '',
        incoming: Boolean(initialData.incoming),
        datetime: initialData.datetime ? new Date(initialData.datetime) : new Date(),
        account: String(initialData.bank_account || initialData.account || ''),
      });
    } else {
      setFormData(emptyForm());
    }
  }, [show, initialData]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.account) return;
    const formattedData = {
      datetime: moment(formData.datetime).format('YYYY-MM-DDTHH:mm:ss'),
      description: formData.description.trim(),
      counterparty: formData.counterparty.trim(),
      category: formData.category,
      amount: Math.abs(parseFloat(formData.amount) || 0).toFixed(2),
      bank_account: parseInt(formData.account, 10),
      incoming: formData.incoming,
    };
    onSubmit(formattedData);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered contentClassName="bg-dark text-light">
      <Modal.Header closeButton closeVariant="white" style={{ borderBottom: '1px solid #333' }}>
        <Modal.Title>{initialData ? 'Edit Transaction' : 'Log money in / out'}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Type</Form.Label>
            <div className="d-flex gap-2">
              <Button
                type="button"
                variant={formData.incoming ? 'success' : 'outline-success'}
                onClick={() => setFormData((prev) => ({ ...prev, incoming: true }))}
                className="flex-grow-1"
              >
                <FaArrowDown className="me-2" /> Money in
              </Button>
              <Button
                type="button"
                variant={!formData.incoming ? 'danger' : 'outline-danger'}
                onClick={() => setFormData((prev) => ({ ...prev, incoming: false }))}
                className="flex-grow-1"
              >
                <FaArrowUp className="me-2" /> Money out
              </Button>
            </div>
          </Form.Group>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Amount (€)</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  min="0"
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  required
                  className="bg-dark text-light border-secondary"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Category</Form.Label>
                <Form.Select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  required
                  className="bg-dark text-light border-secondary"
                >
                  {CATEGORY_CHOICES.map((cat) => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Account</Form.Label>
            <Form.Select
              name="account"
              value={formData.account}
              onChange={handleChange}
              required
              className="bg-dark text-light border-secondary"
            >
              <option value="">Select account</option>
              {bankAccounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.bank} · €{Number(account.total_balance).toFixed(2)}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Who / from where (optional)</Form.Label>
            <Form.Control
              type="text"
              name="counterparty"
              value={formData.counterparty}
              onChange={handleChange}
              placeholder="e.g. Client name, employer, friend"
              className="bg-dark text-light border-secondary"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Notes</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              name="description"
              value={formData.description}
              onChange={handleChange}
              className="bg-dark text-light border-secondary"
            />
          </Form.Group>

          <Form.Group className="mb-1">
            <Form.Label style={{ display: 'block' }}>Date</Form.Label>
            <DatePicker
              selected={formData.datetime}
              onChange={(date) => setFormData((prev) => ({ ...prev, datetime: date }))}
              className="form-control bg-dark text-light border-secondary"
              dateFormat="dd/MM/yyyy"
              required
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: '1px solid #333' }}>
          <Button variant="secondary" onClick={onHide}>Cancel</Button>
          <Button variant="primary" type="submit" style={{ backgroundColor: ACCENT, borderColor: ACCENT }}>
            {initialData ? 'Save' : 'Log transaction'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default TransactionModal;
